import { useEffect, useRef } from "react";
import { AlertTriangle, CloudLightning, Droplets } from "lucide-react";

// Warning-level NWS events that trigger the breaking takeover (per admin spec:
// Tornado / Severe Thunderstorm / Flash Flood Warnings only).
const WARNING_RE = /(tornado|severe thunderstorm|flash flood)\s+warning/i;
export const isTakeoverWarning = (a) => WARNING_RE.test((a && a.title) || "");

function hazard(alert) {
  const t = (alert?.title || "").toLowerCase();
  if (t.includes("tornado")) return { Icon: AlertTriangle, action: "TAKE COVER NOW — lowest floor, interior room, away from windows." };
  if (t.includes("flash flood")) return { Icon: Droplets, action: "TURN AROUND, DON'T DROWN — never drive through flooded roads." };
  if (t.includes("severe thunderstorm")) return { Icon: CloudLightning, action: "MOVE INDOORS — damaging winds and large hail are likely." };
  return { Icon: AlertTriangle, action: "TAKE ACTION NOW and stay with the Georgia Weather Network." };
}

// Full-screen breaking-weather takeover that slams in ahead of the alert board
// when a NEW warning fires. CSS-keyframe driven (encoder-safe). Plays the branded
// attention tone on mount when `playTone` is set (capture page only).
export function AlertTakeover({ alert, emergency = false, playTone = false, toneUrl }) {
  const audioRef = useRef(null);
  useEffect(() => {
    if (!playTone || !toneUrl) return;
    const el = audioRef.current;
    if (!el) return;
    el.volume = 1;
    const p = el.play();
    if (p && p.catch) p.catch(() => {});
  }, [playTone, toneUrl]);

  if (!alert) return null;
  const isEmergency = emergency || alert.tornado_emergency || alert.pds;
  const bannerText = alert.tornado_emergency ? "Tornado Emergency" : alert.pds ? "PDS · Dangerous Situation" : "Breaking Weather";
  const titleText = alert.tornado_emergency ? "TORNADO EMERGENCY" : alert.title;
  const { Icon, action } = hazard(alert);
  const area = alert.area || (Array.isArray(alert.counties) ? alert.counties.slice(0, 6).join(", ") : "Georgia");

  return (
    <div data-testid="alert-takeover" className={`takeover-root absolute inset-0 overflow-hidden ${isEmergency ? "takeover-emergency" : ""}`} style={{ background: isEmergency ? "#1a0000" : "#5c0010" }}>
      {/* Pulsing radial glow + sweeping hazard bars */}
      <div className="takeover-glow absolute inset-0" />
      <div className="takeover-bars absolute inset-0 opacity-25" />
      {isEmergency && <div className="takeover-strobe absolute inset-0" style={{ border: "14px solid #ff1f3d" }} data-testid="emergency-strobe" />}

      {/* Flashing banner */}
      <div className="takeover-banner absolute inset-x-0 top-0 flex items-center justify-center gap-6 py-6 text-white" style={{ background: isEmergency ? "#9b0000" : "#d90429" }}>
        <span className="live-dot" style={{ width: 20, height: 20, background: "#fff" }} />
        <span data-testid="takeover-banner" className="font-oswald text-[58px] font-black uppercase tracking-[0.35em] drop-shadow-[0_4px_18px_rgba(0,0,0,0.5)]">
          {bannerText}
        </span>
        <span className="live-dot" style={{ width: 20, height: 20, background: "#fff" }} />
      </div>

      {/* Center hazard */}
      <div className="relative flex h-full w-full flex-col items-center justify-center px-24 text-center text-white">
        <div className="takeover-icon flex h-[180px] w-[180px] items-center justify-center rounded-full border-[6px] border-[#ffd60a] bg-black/30">
          <Icon className="h-[110px] w-[110px]" style={{ color: "#ffd60a" }} />
        </div>
        <div data-testid="takeover-title" className={`takeover-rise mt-8 font-oswald font-black uppercase leading-[0.9] tracking-wide drop-shadow-[0_12px_40px_rgba(0,0,0,0.5)] ${isEmergency ? "text-[118px] text-[#ffd60a]" : "text-[132px]"}`}>
          {titleText}
        </div>
        {isEmergency && (
          <div className="takeover-rise mt-3 font-oswald text-[34px] font-black uppercase tracking-[0.12em] text-white" style={{ animationDelay: "80ms" }}>
            This is a life-threatening situation
          </div>
        )}
        <div className="takeover-rise mt-5 max-w-[1400px] font-oswald text-[40px] font-bold uppercase tracking-wide text-[#ffd60a]" style={{ animationDelay: "120ms" }}>
          {area}
        </div>
        <div className="takeover-rise mt-8 inline-flex items-center gap-4 rounded-[6px] bg-[#0a0f1c] px-12 py-6" style={{ animationDelay: "240ms" }}>
          <span className="font-oswald text-[44px] font-black uppercase tracking-wide">{action}</span>
        </div>
      </div>

      {/* Network lockup */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 font-oswald text-[26px] font-black uppercase tracking-[0.4em] text-white/80">
        G<span className="text-[#ffd60a]">W</span>N · Georgia Weather Network
      </div>

      {playTone && toneUrl && <audio ref={audioRef} data-testid="takeover-tone" src={toneUrl} preload="auto" />}
    </div>
  );
}
